// Shared UI state types and helpers.
import type { ImageInfo, LineInfo, SpanInfo } from '../engine/worker/protocol.ts';
import { openForViewing, renderPage, type PdfjsDocument, type ViewerHandle } from './pdfjs.ts';

/** CSS px per PDF point at 100 % zoom (AC-UX-7) */
export const BASE_SCALE = 1.5;
/** the zoom steps offered (share of BASE_SCALE) */
export const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4];
export const ZOOM_MIN = 0.5;
/** Fit width and fit page may go below ZOOM_MIN, so a very large page (up to 200 inches) still fits the window. */
export const FIT_MIN = 0.01;
export const ZOOM_MAX = 4;
/**
 * Debug overlay data (each span's glyph boxes as a data attribute, for tests and
 * debugging): off unless local storage has flexpdf-debug-overlay = 1. It costs
 * several MB of page memory on text-heavy pages.
 */
export const DEBUG_OVERLAY = (() => {
  try {
    return localStorage.getItem('flexpdf-debug-overlay') === '1';
  } catch {
    return false;
  }
})();
/** pages farther than this from the viewport are not rendered (their canvases are released) */
export const RENDER_MARGIN_PX = 1500;
export const RENDER_MARGIN = `${RENDER_MARGIN_PX}px`;

export type Status = { kind: 'loading' } | { kind: 'ready' } | { kind: 'busy'; what: string } | { kind: 'error'; message: string };

/** A line being edited: its text in an input over its first piece. */
export interface Editing {
  page: number;
  line: LineInfo;
  /** the line's first piece (where the input sits) */
  span: SpanInfo;
  text: string;
  /** what to select in the text when the editor opens (the double-clicked word); the end when unset */
  select?: [number, number];
  /** "Do it anyway" in debug mode (D-45): cautious refusals and an unverified result are overridden */
  force?: boolean;
  /** the substitute font the user picked (AC-FONTREG-4) */
  face?: string;
}

/**
 * The line's text range of the word at glyph `glyph` of piece `piece`, when the
 * line's text is exactly its pieces' characters one per glyph (else undefined:
 * ligatures or added spaces make the mapping uncertain).
 */
export function wordAt(line: LineInfo, pieces: SpanInfo[], piece: number, glyph: number): [number, number] | undefined {
  if (pieces.some((x) => [...x.text].length !== x.quads.length) || pieces.map((x) => x.text).join('') !== line.text) return undefined;
  const chars = [...line.text];
  let at = glyph;
  for (let k = 0; k < piece; k++) at += pieces[k].quads.length;
  if (at < 0 || at >= chars.length) return undefined;
  const word = (c: string) => /\S/.test(c);
  if (!word(chars[at])) return [at, at];
  let a = at;
  let b = at + 1;
  while (a > 0 && word(chars[a - 1])) a--;
  while (b < chars.length && word(chars[b])) b++;
  // As offsets into the string (UTF-16), which is what the input selects by.
  const offset = (n: number) => chars.slice(0, n).join('').length;
  return [offset(a), offset(b)];
}

/** A CSS font close to the PDF font, for the editor that sits over a line (the real glyphs are written on apply). */
export function cssFontFor(fontName: string): { family: string; weight: number; style: string } {
  const n = fontName.replace(/^[A-Z]{6}\+/, '');
  const family = /courier|mono|consol/i.test(n) ? 'ui-monospace, "Courier New", monospace' : /times|serif|roman|georgia|garamond|minion|cmr|cmu|lmroman|libertin|charis|palatino|book/i.test(n) && !/sans/i.test(n) ? '"Times New Roman", Liberation Serif, serif' : 'Helvetica, Arial, "Liberation Sans", sans-serif';
  return { family, weight: /bold|black|heavy|semibold|demi/i.test(n) ? 700 : 400, style: /italic|oblique/i.test(n) ? 'italic' : 'normal' };
}

/** A selected text line (all its pieces) or image. */
export type Item = { kind: 'span'; line: LineInfo; pieces: SpanInfo[] } | { kind: 'image'; image: ImageInfo };
/** What the toolbar acts on: one or more lines and images, all on one page (AC-SEL-1). */
export interface Selection {
  page: number;
  items: Item[];
}
export const sameItem = (a: Item, b: Item) => (a.kind === 'span' && b.kind === 'span' ? a.line.index === b.line.index : a.kind === 'image' && b.kind === 'image' && a.image.index === b.image.index);
/** The selection with `item` added, or removed if it was in it (Shift+click). */
export function toggled(sel: Selection | undefined, page: number, item: Item): Selection | undefined {
  if (!sel || sel.page !== page) return { page, items: [item] };
  const items = sel.items.some((x) => sameItem(x, item)) ? sel.items.filter((x) => !sameItem(x, item)) : [...sel.items, item];
  return items.length ? { page, items } : undefined;
}
export const describeItems = (items: Item[]) => (items.length === 1 ? (items[0].kind === 'span' ? `"${items[0].line.text}"` : 'the image') : `${items.length} objects`);

/** An open document. */
export interface Session {
  name: string;
  /** the version shown and edited (the original until the first edit) */
  bytes: Uint8Array;
  password?: string;
  viewer: { handle: ViewerHandle; doc: PdfjsDocument };
  /** size of each page in PDF points, rotation applied (CSS size = this × the scale) */
  sizes: { width: number; height: number }[];
  /** why editing and export are disabled */
  viewOnly?: string;
  /** the view-only state comes from permission flags; the owner password lifts it */
  restricted: boolean;
  /** the editing engine could not read the file at all; pdf.js shows it */
  engineless: boolean;
}

export interface PasswordPrompt {
  name: string;
  data: Uint8Array;
  wrong: boolean;
  /** asking for the owner password to lift restrictions */
  owner: boolean;
}

export async function pageSizes(doc: PdfjsDocument): Promise<{ width: number; height: number }[]> {
  const sizes = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const vp = (await doc.getPage(i)).getViewport({ scale: 1 });
    sizes.push({ width: vp.width, height: vp.height });
  }
  return sizes;
}

/** Opens `data` in pdf.js; undefined + reason when it cannot. */
export async function openViewer(data: Uint8Array, password?: string): Promise<{ handle: ViewerHandle; doc: PdfjsDocument } | { error: unknown }> {
  const handle = openForViewing(data, password);
  try {
    return { handle, doc: await handle.promise };
  } catch (error) {
    await handle.destroy();
    return { error };
  }
}

/** A small picture of page 1 (PNG, 160 px wide) for the recent files. */
export async function thumbnail(doc: PdfjsDocument): Promise<Blob | undefined> {
  const page = await doc.getPage(1);
  const width = page.getViewport({ scale: 1 }).width;
  const canvas = document.createElement('canvas');
  await renderPage(doc, 1, canvas, 160 / width).done;
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b ?? undefined), 'image/png'));
}
